import { Minus, Plus } from "lucide-react";
import type { Ref } from "react";

import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { stepCount } from "@/features/pages/utils/imageCounts";

interface Props {
    /** Null is a count the patient has not answered yet. It shows as an empty box. */
    value: number | null;
    min: number;
    max: number;
    /** Names the count for assistive technology, such as "Picture A: Moisture only". */
    label: string;
    onChange: (value: number | null) => void;
    name?: string;
    onBlur?: () => void;
    inputRef?: Ref<HTMLInputElement>;
}

/**
 * Minus, a number box and plus. Typing or pressing stays within min and max. A blank is not 0: the first
 * plus gives min + 1 and the first minus gives min, so pressing a button always records an answer.
 * Used by the `count` input and by the picture rows of an image counts page.
 */
const CountStepper = ({ value, min, max, label, onChange, name, onBlur, inputRef }: Props) => {
    const copy = useFormPageCopy();

    return (
        <div className="flex items-center gap-1 shrink-0">
            <button
                type="button"
                aria-label={copy.countDecrease(label)}
                data-testid={name ? `form-count-${name}-minus` : undefined}
                disabled={value !== null && value <= min}
                onClick={() => onChange(stepCount(value, -1, min, max))}
                className="flex justify-center items-center border dark:border-slate-600 rounded-full w-11 h-11 disabled:opacity-40"
            >
                <Minus size={18} aria-hidden />
            </button>
            <input
                ref={inputRef}
                name={name}
                data-testid={name ? `form-count-${name}` : undefined}
                type="number"
                inputMode="numeric"
                min={min}
                max={max}
                step={1}
                aria-label={label}
                value={value ?? ""}
                onBlur={() => {
                    // Leaving the box empty means 0.
                    if (value === null) onChange(Math.max(min, 0));
                    onBlur?.();
                }}
                onChange={(event) => {
                    const raw = event.target.value.trim();
                    if (raw === "") return onChange(null);
                    const next = Number(raw);
                    if (Number.isFinite(next)) onChange(Math.min(Math.max(Math.round(next), min), max));
                }}
                className="bg-white dark:bg-slate-900 border dark:border-slate-600 rounded-md w-14 h-11 text-lg text-center tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <button
                type="button"
                aria-label={copy.countIncrease(label)}
                data-testid={name ? `form-count-${name}-plus` : undefined}
                disabled={value === null ? min + 1 > max : value >= max}
                onClick={() => onChange(stepCount(value, 1, min, max))}
                className="flex justify-center items-center border dark:border-slate-600 rounded-full w-11 h-11 disabled:opacity-40"
            >
                <Plus size={18} aria-hidden />
            </button>
        </div>
    );
};

export default CountStepper;
